// ─────────────────────────────────────────────
//  presence — who is actually using the app right now.
//
//  Firebase Auth cannot answer this. It knows a credential is valid, not that
//  a person has the app open, so "who is signed in" has to be observed: the
//  client stamps users/{uid}.lastSeenAt while the app is in the foreground, and
//  everything here is derived from that one field.
//
//  DELIBERATELY COARSE. The heartbeat is minutes, not seconds, and nothing
//  records what anyone is doing or where they are — this answers "is my team on
//  the app" and "did this person ever start using it", and nothing finer. A
//  workforce tool that tracks activity in detail becomes a surveillance tool,
//  and staff who suspect one stop carrying the phone.
//
//  Pure — no Firebase.
// ─────────────────────────────────────────────

// Stamp roughly every 4 minutes while the app is visible.
export const HEARTBEAT_MS = 4 * 60 * 1000;
// Count someone online for 10 — wider than two beats on purpose. A phone that
// slept through one interval is not a person who logged out, and flapping
// between online and offline makes the whole panel untrustworthy.
export const ONLINE_WINDOW_MS = 10 * 60 * 1000;

/**
 * The same field arrives as a Firestore Timestamp, a Date, an epoch number, or
 * an ISO string depending on whether it came from the server, a local echo, or
 * a fixture. Returns null — never 0 — for anything unusable: Number(null) === 0
 * would render as 1970 and read as a real (very old) sighting.
 */
export function toMillis(value) {
  if (value == null) return null;
  if (typeof value.toMillis === "function") return value.toMillis();
  if (typeof value.seconds === "number") return value.seconds * 1000;
  if (value instanceof Date) {
    const t = value.getTime();
    return Number.isFinite(t) ? t : null;
  }
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string") {
    const t = Date.parse(value);
    return Number.isFinite(t) ? t : null;
  }
  return null;
}

export function isOnline(lastSeenAt, now = Date.now()) {
  const t = toMillis(lastSeenAt);
  if (t == null) return false;
  return now - t <= ONLINE_WINDOW_MS;
}

const MIN = 60 * 1000;
const HOUR = 60 * MIN;
const DAY = 24 * HOUR;

// "never" is kept distinct from any elapsed time: a user who never signed in is
// an adoption problem, not an idle one, and the two need different follow-ups.
export function lastSeenLabel(lastSeenAt, now = Date.now()) {
  const t = toMillis(lastSeenAt);
  if (t == null) return "never";
  const delta = Math.max(0, now - t);
  if (delta < MIN) return "just now";
  if (delta < HOUR) return `${Math.floor(delta / MIN)}m ago`;
  if (delta < DAY) return `${Math.floor(delta / HOUR)}h ago`;
  if (delta < 30 * DAY) return `${Math.floor(delta / DAY)}d ago`;
  return new Date(t).toLocaleDateString();
}

/**
 * Presence rows for an admin list, ordered online-first then most-recently-seen.
 * @returns {{rows: object[], onlineCount: number, neverSignedInCount: number, total: number}}
 */
export function summarizePresence(users, now = Date.now()) {
  const rows = (users || []).map((u) => {
    const seen = toMillis(u?.lastSeenAt);
    return {
      ...u,
      lastSeenMs: seen,
      online: seen != null && now - seen <= ONLINE_WINDOW_MS,
      label: lastSeenLabel(u?.lastSeenAt, now),
      neverSignedIn: seen == null,
    };
  });
  rows.sort((a, b) => {
    if (a.online !== b.online) return a.online ? -1 : 1;
    // Never-seen sinks below anyone with a real sighting.
    if ((a.lastSeenMs == null) !== (b.lastSeenMs == null)) return a.lastSeenMs == null ? 1 : -1;
    if (a.lastSeenMs !== b.lastSeenMs) return (b.lastSeenMs || 0) - (a.lastSeenMs || 0);
    return String(a.name || "").localeCompare(String(b.name || ""));
  });
  return {
    rows,
    onlineCount: rows.filter((r) => r.online).length,
    neverSignedInCount: rows.filter((r) => r.neverSignedIn).length,
    total: rows.length,
  };
}
